import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  Plus, Trash2, Target, AlertTriangle, Link2, Paperclip, Unlink,
  ArrowLeftRight, Eye, Search, CheckCircle2, XCircle
} from 'lucide-react';

export function AddCauseDialog({ open, dialog, causeText, setCauseText, onAdd, onAutoClassify, onClose }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white text-slate-900 max-w-md w-[95vw]">
        <DialogHeader>
          <DialogTitle className="text-slate-900">Add Cause — {dialog?.catName}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <Input value={causeText} onChange={e => setCauseText(e.target.value)} placeholder="Describe the cause..."
            className="bg-white border-slate-300 text-slate-900" data-testid="cause-text-input"
            onKeyDown={e => e.key === 'Enter' && onAdd()} autoFocus />
          <div className="flex gap-2">
            <Button onClick={onAdd} className="bg-blue-600 hover:bg-blue-700 text-white flex-1" disabled={!causeText.trim()} data-testid="add-cause-submit">
              <Plus className="w-4 h-4 mr-1.5" /> Add Cause
            </Button>
            <Button variant="outline" onClick={async () => {
              if (!causeText.trim()) return;
              const result = await onAutoClassify(causeText);
              if (result?.category_name) {
                alert(`Suggested category: ${result.category_name} (${Math.round(result.confidence * 100)}% confidence)`);
              }
            }} className="border-slate-300 text-slate-700" data-testid="auto-classify-btn">
              <Search className="w-4 h-4 mr-1" /> Classify
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function AddSubCauseDialog({ open, dialog, subText, setSubText, onAdd, onClose }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white text-slate-900 max-w-md w-[95vw]">
        <DialogHeader>
          <DialogTitle className="text-slate-900">Add Sub-Cause</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <p className="text-xs text-slate-500">Parent cause: <span className="font-medium text-slate-700">{dialog?.causeText}</span></p>
          <Input value={subText} onChange={e => setSubText(e.target.value)} placeholder="Describe the sub-cause..."
            className="bg-white border-slate-300 text-slate-900" data-testid="sub-cause-text-input"
            onKeyDown={e => e.key === 'Enter' && onAdd()} autoFocus />
          <Button onClick={onAdd} className="bg-blue-600 hover:bg-blue-700 text-white w-full" disabled={!subText.trim()} data-testid="add-sub-cause-submit">
            <Plus className="w-4 h-4 mr-1.5" /> Add Sub-Cause
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function AddCategoryDialog({ open, name, setName, color, setColor, onAdd, onClose }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white text-slate-900 max-w-sm w-[95vw]">
        <DialogHeader>
          <DialogTitle className="text-slate-900">Add Custom Category</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <Input value={name} onChange={e => setName(e.target.value)} placeholder="Category name..."
            className="bg-white border-slate-300 text-slate-900" data-testid="category-name-input" autoFocus />
          <div className="flex items-center gap-3">
            <label className="text-sm text-slate-600">Color:</label>
            <input type="color" value={color} onChange={e => setColor(e.target.value)} className="w-8 h-8 rounded cursor-pointer" />
          </div>
          <Button onClick={onAdd} className="bg-blue-600 hover:bg-blue-700 text-white w-full" disabled={!name.trim()} data-testid="add-category-submit">
            <Plus className="w-4 h-4 mr-1.5" /> Add Category
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function CauseDetailDialog({ open, dialog, canEdit, onToggleRoot, onToggleContributing, onOpenEvidence, onDeleteCause, onClose }) {
  if (!dialog) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white text-slate-900 max-w-md w-[95vw]">
        <DialogHeader>
          <DialogTitle className="text-slate-900 flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: dialog.cat.color }} />
            {dialog.cat.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <p className="text-sm font-medium text-slate-800">{dialog.cause.text}</p>
            <p className="text-[10px] text-slate-400 mt-1">Added by {dialog.cause.added_by} on {dialog.cause.added_at?.split('T')[0]}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => { onToggleRoot(dialog.cause.id, dialog.cause.is_root_cause); onClose(); }}
              className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium border ${dialog.cause.is_root_cause ? 'bg-red-50 border-red-200 text-red-700' : 'border-slate-200 text-slate-600 hover:border-red-200'}`}>
              <Target className="w-3.5 h-3.5 inline mr-1" /> {dialog.cause.is_root_cause ? 'Root Cause' : 'Mark as Root Cause'}
            </button>
            <button onClick={() => { onToggleContributing(dialog.cause.id, dialog.cause.is_contributing_factor); onClose(); }}
              className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium border ${dialog.cause.is_contributing_factor ? 'bg-amber-50 border-amber-200 text-amber-700' : 'border-slate-200 text-slate-600 hover:border-amber-200'}`}>
              <AlertTriangle className="w-3.5 h-3.5 inline mr-1" /> {dialog.cause.is_contributing_factor ? 'Contributing' : 'Mark Contributing'}
            </button>
          </div>
          <Button variant="outline" size="sm" className="w-full border-slate-300 text-slate-700"
            onClick={() => { onOpenEvidence(dialog.cause.id, dialog.cause.evidence_ids || []); onClose(); }}>
            <Paperclip className="w-3.5 h-3.5 mr-1.5" /> Link Evidence ({(dialog.cause.evidence_ids || []).length})
          </Button>
          {dialog.cause.sub_causes?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-600 mb-1">Sub-Causes:</p>
              {dialog.cause.sub_causes.map(sub => (
                <div key={sub.id} className="flex items-center gap-2 pl-2 py-1 text-xs text-slate-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                  {sub.text}
                </div>
              ))}
            </div>
          )}
          {canEdit && (
            <Button variant="outline" size="sm" className="text-red-500 border-red-200 hover:bg-red-50 w-full"
              onClick={() => onDeleteCause(dialog.cat.id, dialog.cause.id)}>
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete Cause
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function EvidenceLinkDialog({ open, dialog, evidenceList, evidenceLoading, onToggle, onClose }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white text-slate-900 max-w-md w-[95vw]">
        <DialogHeader>
          <DialogTitle className="text-slate-900 flex items-center gap-2">
            <Paperclip className="w-5 h-5 text-blue-500" />
            Link Evidence to Cause
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2 mt-2 max-h-[400px] overflow-y-auto">
          {evidenceLoading ? (
            <div className="text-center py-6 text-slate-400">Loading evidence...</div>
          ) : evidenceList.length === 0 ? (
            <div className="text-center py-6 text-slate-400">No evidence uploaded for this investigation yet.</div>
          ) : evidenceList.map(ev => {
            const isLinked = (dialog?.currentIds || []).includes(ev.id);
            return (
              <div key={ev.id} onClick={() => onToggle(ev.id)}
                className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${isLinked ? 'border-blue-300 bg-blue-50' : 'border-slate-200 hover:border-blue-200'}`}
                data-testid={`evidence-item-${ev.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-800 truncate">{ev.file_name}</div>
                  <div className="text-xs text-slate-500">{ev.file_category} {ev.description ? `- ${ev.description}` : ''}</div>
                </div>
                {isLinked ? (
                  <span className="shrink-0 ml-2 px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-200 text-blue-800">LINKED</span>
                ) : (
                  <Link2 className="w-4 h-4 text-slate-300 shrink-0 ml-2" />
                )}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function CompareDialog({ open, onClose, investigations, compareId, setCompareId, onCompare, compareLoading, compareResult }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white text-slate-900 max-w-3xl w-[95vw] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-slate-900 flex items-center gap-2">
            <ArrowLeftRight className="w-5 h-5 text-indigo-500" />
            Compare Fishbone Diagrams
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label className="text-xs font-medium text-slate-600 mb-1 block">Compare with:</label>
              <select value={compareId} onChange={e => setCompareId(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white text-slate-900"
                data-testid="compare-select">
                <option value="">Select an investigation...</option>
                {investigations.map(inv => (
                  <option key={inv.investigation_id} value={inv.investigation_id}>
                    {inv.investigation_number} ({inv.sector?.toUpperCase()}) - {inv.status}
                  </option>
                ))}
              </select>
            </div>
            <Button onClick={onCompare} disabled={!compareId || compareLoading}
              className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="compare-submit">
              {compareLoading ? 'Comparing...' : 'Compare'}
            </Button>
          </div>
          {compareResult && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="text-xs font-bold text-blue-700">{compareResult.investigation_1.number}</div>
                  <div className="text-[10px] text-blue-600">{compareResult.investigation_1.sector?.toUpperCase()} | {compareResult.investigation_1.total_causes} causes</div>
                </div>
                <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <div className="text-xs font-bold text-purple-700">{compareResult.investigation_2.number}</div>
                  <div className="text-[10px] text-purple-600">{compareResult.investigation_2.sector?.toUpperCase()} | {compareResult.investigation_2.total_causes} causes</div>
                </div>
              </div>
              {compareResult.common_causes?.length > 0 && (
                <div>
                  <h4 className="text-sm font-bold text-green-700 mb-2 flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4" /> Common Causes ({compareResult.common_causes.length})
                  </h4>
                  <div className="space-y-1">
                    {compareResult.common_causes.map((c) => (
                      <div key={`common-${c.text}`} className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded-lg text-sm">
                        <span className="w-2 h-2 rounded-full bg-green-500 shrink-0" />
                        <span className="text-slate-800 flex-1">{c.text}</span>
                        <span className="text-[10px] text-slate-500 shrink-0">{c.category_1}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {compareResult.similar_causes?.length > 0 && (
                <div>
                  <h4 className="text-sm font-bold text-amber-700 mb-2 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" /> Similar Causes ({compareResult.similar_causes.length})
                  </h4>
                  <div className="space-y-1">
                    {compareResult.similar_causes.map((c) => (
                      <div key={`sim-${c.cause_1}-${c.cause_2}`} className="p-2 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-blue-700 flex-1">{c.cause_1}</span>
                          <span className="text-[10px] bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded-full font-bold">{Math.round(c.similarity * 100)}%</span>
                          <span className="text-purple-700 flex-1 text-right">{c.cause_2}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <h4 className="text-xs font-bold text-blue-700 mb-1">Unique to {compareResult.investigation_1.number} ({compareResult.unique_to_1?.length || 0})</h4>
                  <div className="space-y-1 max-h-[200px] overflow-y-auto">
                    {(compareResult.unique_to_1 || []).map((c) => (
                      <div key={`u1-${c.text}`} className="text-xs p-1.5 bg-slate-50 border border-slate-200 rounded text-slate-700">
                        {c.text} <span className="text-slate-400">({c.category})</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="text-xs font-bold text-purple-700 mb-1">Unique to {compareResult.investigation_2.number} ({compareResult.unique_to_2?.length || 0})</h4>
                  <div className="space-y-1 max-h-[200px] overflow-y-auto">
                    {(compareResult.unique_to_2 || []).map((c) => (
                      <div key={`u2-${c.text}`} className="text-xs p-1.5 bg-slate-50 border border-slate-200 rounded text-slate-700">
                        {c.text} <span className="text-slate-400">({c.category})</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function PatternsDialog({ open, onClose, patterns }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white text-slate-900 max-w-2xl w-[95vw] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-slate-900 flex items-center gap-2">
            <Eye className="w-5 h-5 text-teal-500" />
            Cross-Investigation Patterns
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          {!patterns ? (
            <div className="text-center py-6 text-slate-400">Loading patterns...</div>
          ) : (
            <>
              <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">{patterns.summary}</p>
              {patterns.top_categories?.length > 0 && (
                <div>
                  <h4 className="text-sm font-bold text-slate-800 mb-2">Most Active Categories</h4>
                  <div className="flex flex-wrap gap-2">
                    {patterns.top_categories.map((c) => (
                      <span key={c.name} className="px-3 py-1.5 rounded-full text-xs font-semibold bg-teal-50 border border-teal-200 text-teal-800">
                        {c.name} ({c.count})
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {patterns.patterns?.length > 0 ? (
                <div>
                  <h4 className="text-sm font-bold text-slate-800 mb-2">Recurring Causes Across Investigations</h4>
                  <div className="space-y-2">
                    {patterns.patterns.map((p) => (
                      <div key={`pat-${p.cause}`} className="flex items-center justify-between p-3 border border-slate-200 rounded-lg" data-testid={`pattern-${p.cause}`}>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-slate-800">{p.cause}</div>
                          <div className="text-xs text-slate-500">{p.category} | {p.occurrences} investigations</div>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0 ml-2">
                          {p.is_root_cause_anywhere && <span className="px-1.5 py-0.5 text-[10px] font-bold bg-red-100 text-red-700 rounded">ROOT</span>}
                          {p.is_contributing_anywhere && <span className="px-1.5 py-0.5 text-[10px] font-bold bg-amber-100 text-amber-700 rounded">CONTRIB</span>}
                          <span className="px-2 py-0.5 text-[10px] font-bold bg-teal-100 text-teal-700 rounded-full">{p.occurrences}x</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-400 text-center py-4">No recurring patterns found yet. Add causes to more fishbone diagrams to detect patterns.</p>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
